// Pure synonym/plural chip-selection machinery for the SciSearch AI review stage.
// Extracted from reducer.js: everything here is stateless computation over the
// interpretation payload (termSynonyms/termPlurals/termPluralSources) and selection
// maps -- no action types, no store shape. The reducer owns state transitions and
// calls in here for the heavy lifting.
import { isExcludedTerm } from './termSides';

/** Default same-side predicate: with no exclusions, every parent term syncs together (legacy behaviour). */
const ALLOW_ALL_TERMS = () => true;

/**
 * Add or remove one `synonym` under `term` in a per-term explicit-tracking map (case-insensitive),
 * returning a new map (or the same reference when nothing changes). Used for
 * `explicitlySelectedSynonyms` (chips the user turned ON that were off by default). An emptied
 * term key is dropped so the map stays compact.
 */
export function toggleExplicitEntry(prev, term, synonym, shouldContain) {
  const map = prev || {};
  const forTerm = map[term] || [];
  const synLow = synonym.toLowerCase();
  const has = forTerm.some(s => s.toLowerCase() === synLow);
  let nextForTerm;
  if (shouldContain) {
    nextForTerm = has ? forTerm : [...forTerm, synonym];
  } else {
    nextForTerm = has
      ? forTerm.filter(s => s.toLowerCase() !== synLow)
      : forTerm;
  }
  if (nextForTerm === forTerm) return map;
  if (nextForTerm.length === 0) {
    if (!(term in map)) return map;
    const copy = { ...map };
    delete copy[term];
    return copy;
  }
  return { ...map, [term]: nextForTerm };
}

/** Case-insensitive unique strings; first occurrence wins. */
export function uniqStringsCi(strings) {
  const seen = new Set();
  const out = [];
  for (const s of strings) {
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

/**
 * Per-term default selection for the synonym modal (top-N with same-side union).
 *
 * Each parent term contributes its top-`limit` related terms — the API returns them in LlmRank
 * order — after dropping shared-across-side labels. Those top-N labels are unioned PER SIDE, and
 * every term then selects the labels from its OWN full list that fall in its side's union. So a
 * related term that is top-N under one term is selected under every same-side term that carries
 * it (consistent with the existing cross-term toggle propagation in applySynonymLabelsToggle). A
 * term therefore shows at least its own top-N, plus any lower-ranked labels it shares with a
 * sibling's top-N.
 *
 * @param {Record<string, string[]>} termSynonyms - rank-ordered related terms per parent term
 * @param {Set<string>} sharedLowers - lowercased labels shared across sides (seeded OFF)
 * @param {Record<string, 'include' | 'exclude'>} sideMap
 * @param {number} limit
 * @returns {Record<string, string[]>} term -> selected labels (verbatim casing, order preserved)
 */
export function seedTopNSelection(termSynonyms, sharedLowers, sideMap, limit) {
  const nonSharedByTerm = Object.fromEntries(
    Object.entries(termSynonyms || {}).map(([t, syns]) => [
      t,
      (Array.isArray(syns) ? syns : []).filter(
        s => !sharedLowers.has(s.toLowerCase())
      ),
    ])
  );
  const onLowersBySide = { include: new Set(), exclude: new Set() };
  for (const [term, syns] of Object.entries(nonSharedByTerm)) {
    const side = isExcludedTerm(sideMap, term) ? 'exclude' : 'include';
    for (const s of syns.slice(0, limit)) {
      onLowersBySide[side].add(s.toLowerCase());
    }
  }
  return Object.fromEntries(
    Object.entries(nonSharedByTerm).map(([term, syns]) => {
      const onSet = isExcludedTerm(sideMap, term)
        ? onLowersBySide.exclude
        : onLowersBySide.include;
      return [term, syns.filter(s => onSet.has(s.toLowerCase()))];
    })
  );
}

/**
 * Default plural-variant selection that respects the top-N synonym default.
 *
 * A plural is dropped from the default ONLY when every source that produced it is a synonym that
 * is default-OFF (beyond the term top-N). Otherwise it stays on — that covers base-term plurals,
 * plurals of default-on synonyms, and plurals whose source key is a derived singular head that is
 * not itself a listed synonym (e.g. "mouse model" -> "mouse models"). This keeps the "synonym off
 * => its sourced plurals off" invariant the toggle machinery enforces, without stripping plurals
 * that merely share a head with something. Variants not attributed to any source are kept.
 *
 * When `selectedSynonymsByTerm` is null (no synonyms seeded this dispatch) or a term has no
 * source attribution, this falls back to the legacy "all non-shared on" behaviour.
 *
 * @returns {Record<string, string[]>} term -> selected plural variants
 */
export function seedPluralSelectionForTopN(
  termPlurals,
  termPluralSources,
  termSynonyms,
  selectedSynonymsByTerm,
  sharedLowers
) {
  return Object.fromEntries(
    Object.entries(termPlurals || {}).map(([term, vars]) => {
      const nonShared = (Array.isArray(vars) ? vars : []).filter(
        v => !sharedLowers.has(v.toLowerCase())
      );
      const sources = termPluralSources?.[term];
      if (!selectedSynonymsByTerm || !sources || typeof sources !== 'object') {
        return [term, nonShared];
      }
      const synLowers = new Set(
        (termSynonyms?.[term] || []).map(s => s.toLowerCase())
      );
      const onSyn = new Set(
        (selectedSynonymsByTerm[term] || []).map(s => s.toLowerCase())
      );
      const seenLowers = new Set();
      const allowedLowers = new Set();
      for (const [sourceKey, variants] of Object.entries(sources)) {
        const sk = sourceKey.toLowerCase();
        // A source blocks its variants only if it is a listed synonym that is default-off.
        const isDefaultOffSynonym = synLowers.has(sk) && !onSyn.has(sk);
        for (const v of variants || []) {
          seenLowers.add(v.toLowerCase());
          if (!isDefaultOffSynonym) allowedLowers.add(v.toLowerCase());
        }
      }
      return [
        term,
        nonShared.filter(v => {
          const low = v.toLowerCase();
          return !seenLowers.has(low) || allowedLowers.has(low);
        }),
      ];
    })
  );
}

/**
 * Lowercased plural labels that would GENUINELY conflict across sides (SCIL-17815).
 *
 * The SCIL-16099 blanket rule ("any label listed on both an inclusion and an exclusion
 * parent seeds off") over-fires now that related terms are opt-in: symmetric expansions
 * (e.g. "cancer excluding oncology", where each term lists the other as a related term)
 * put every label on both sides and strip the base-term plurals the search needs — the
 * PRD requires "NOT oncology" to exclude "oncologies" too. Only two situations are real
 * conflicts for plurals:
 *  - the label would seed SELECTED on both sides (same string included and excluded), or
 *  - the label equals a parent term on the OPPOSITE side (selecting it would contradict
 *    the query's own structure, e.g. plural "cancers" against NOT "cancers").
 * A label merely listed unselected on the other side is not a conflict — it only enters
 * the query if the user selects it, at which point the both-sides warning fires.
 *
 * @param {Record<string, string[]>} termPlurals
 * @param {Record<string, Record<string, string[]>>} termPluralSources
 * @param {Record<string, string[]>} termSynonyms
 * @param {Record<string, string[]> | null} selectedSynonymsByTerm
 * @param {Record<string, 'include' | 'exclude'>} sideMap
 * @returns {Set<string>}
 */
export function conflictingPluralLowers(
  termPlurals,
  termPluralSources,
  termSynonyms,
  selectedSynonymsByTerm,
  sideMap
) {
  // What WOULD seed selected per term with no cross-side filtering at all.
  const candidates = seedPluralSelectionForTopN(
    termPlurals,
    termPluralSources,
    termSynonyms,
    selectedSynonymsByTerm,
    new Set()
  );

  const bySide = { include: new Set(), exclude: new Set() };
  for (const [term, vars] of Object.entries(candidates)) {
    const side = isExcludedTerm(sideMap, term) ? 'exclude' : 'include';
    for (const v of vars || []) bySide[side].add(v.toLowerCase());
  }

  const conflicts = new Set();
  for (const label of bySide.include) {
    if (bySide.exclude.has(label)) conflicts.add(label);
  }
  for (const [term, vars] of Object.entries(candidates)) {
    const termExcluded = isExcludedTerm(sideMap, term);
    for (const v of vars || []) {
      const low = v.toLowerCase();
      const parentSide = sideMap?.[low];
      if (!parentSide) continue;
      if ((parentSide === 'exclude') !== termExcluded) conflicts.add(low);
    }
  }
  return conflicts;
}

export function dedupeTermSynonyms(ts) {
  return Object.fromEntries(
    Object.entries(ts).map(([t, syns]) => [t, uniqStringsCi(syns)])
  );
}

export function dedupeTermPlurals(tp) {
  return Object.fromEntries(
    Object.entries(tp).map(([t, vars]) => [t, uniqStringsCi(vars)])
  );
}

export function dedupeTermPluralSources(tps) {
  if (!tps || typeof tps !== 'object') return tps;
  return Object.fromEntries(
    Object.entries(tps).map(([baseTerm, sources]) => [
      baseTerm,
      Object.fromEntries(
        Object.entries(sources).map(([src, vars]) => [src, uniqStringsCi(vars)])
      ),
    ])
  );
}

/** Plural variants attributed to synonym sources (any source key other than the row term). */
export function synonymSourcedPluralVariants(term, sources) {
  if (!sources || typeof sources !== 'object') return [];
  return Object.entries(sources)
    .filter(([sourceKey]) => sourceKey.toLowerCase() !== term.toLowerCase())
    .flatMap(([, variants]) => variants);
}

/** Row + master toggles mirror chip selections: on iff that row has ≥1 chip; master on iff any row has chips. */
export function syncSynonymTogglesFromSelections(
  selectedSynonyms,
  termSynonyms,
  prevTermEnabled
) {
  const ts =
    termSynonyms && typeof termSynonyms === 'object' ? termSynonyms : {};
  const keys = Object.keys(ts);
  const termEnabled = { ...prevTermEnabled };
  for (const t of keys) {
    termEnabled[t] = (selectedSynonyms[t] || []).length > 0;
  }
  const synonymsEnabled = keys.some(
    t => (selectedSynonyms[t] || []).length > 0
  );
  return { termEnabled, synonymsEnabled };
}

export function syncPluralTogglesFromSelections(
  selectedPluralVariants,
  termPlurals,
  prevPluralTermEnabled
) {
  const tp = termPlurals && typeof termPlurals === 'object' ? termPlurals : {};
  const keys = Object.keys(tp);
  const pluralTermEnabled = { ...prevPluralTermEnabled };
  for (const t of keys) {
    pluralTermEnabled[t] = (selectedPluralVariants[t] || []).length > 0;
  }
  const pluralsEnabled = keys.some(
    t => (selectedPluralVariants[t] || []).length > 0
  );
  return { pluralTermEnabled, pluralsEnabled };
}

export function toLowerSet(strings) {
  return new Set((strings || []).map(s => s.toLowerCase()));
}

function labelsInListByLower(list, labelLowers) {
  if (!Array.isArray(list) || !labelLowers || labelLowers.size === 0) return [];
  return list.filter(item => labelLowers.has(item.toLowerCase()));
}

/** Synonym list chips + source keys in termPluralSources that match linked labels (per base term). */
function labelsAndSourceKeysForPluralSideEffects(
  term,
  termSynonyms,
  termPluralSources,
  linkedLabelLowers
) {
  const synList = termSynonyms[term] || [];
  const fromSyns = labelsInListByLower(synList, linkedLabelLowers);
  const sources = termPluralSources?.[term] || {};
  const fromSourceKeys = [];
  for (const sourceKey of Object.keys(sources)) {
    if (linkedLabelLowers.has(sourceKey.toLowerCase())) {
      fromSourceKeys.push(sourceKey);
    }
  }
  return uniqStringsCi([...fromSyns, ...fromSourceKeys]);
}

/**
 * Expand linked chip labels for synonym/plural toggles using only:
 * - the seed chip text(s), and
 * - edges in termPluralSources (plus guarded enqueue rules).
 *
 * Does not enqueue row/base-term keys as search tokens (avoids "cancer" / "tumors"
 * matching unrelated chips on other rows).
 */
function resolveLinkedLabelLowersFromPluralSources(
  interp,
  seedLabels,
  allowTerm = ALLOW_ALL_TERMS
) {
  const queue = [...toLowerSet(seedLabels)];
  const visited = new Set();
  const termPlurals = interp?.termPlurals || {};
  const termPluralSources = interp?.termPluralSources || {};

  const rowHasVariantChip = (baseTerm, labelLower) => {
    const row = termPlurals[baseTerm] || [];
    return row.some(v => v.toLowerCase() === labelLower);
  };

  while (queue.length > 0) {
    const token = queue.shift();
    if (visited.has(token)) continue;
    visited.add(token);

    for (const [baseTerm, sources] of Object.entries(termPluralSources)) {
      if (!allowTerm(baseTerm)) continue;
      const baseLower = baseTerm.toLowerCase();
      for (const [sourceKey, variants] of Object.entries(sources || {})) {
        const sourceLower = sourceKey.toLowerCase();
        if (sourceLower === token) {
          if (rowHasVariantChip(baseTerm, baseLower)) {
            // Phrase source keys (e.g. "whole mouse") must not enqueue the row base term
            // (e.g. "mice"); that would add the base token to the linked set and strip every
            // chip whose label matches "mice"/"mouse" via applyPluralLabelsToggle, not just
            // phrase-paired plurals (handled in applySynonymTogglePluralSideEffects).
            const sourceKeyIsPhrase = /\s/.test(String(sourceKey).trim());
            if (!sourceKeyIsPhrase) {
              queue.push(baseLower);
            }
          }
        }
        const variantHit = (variants || []).find(
          v => v.toLowerCase() === token
        );
        if (variantHit) {
          // Only walk to sourceKey when the hit variant *is* that key (e.g. mouse↔Mouse).
          // Do not promote sourceKey when the hit is a different phrase (e.g. "whole mouse"
          // under Mouse) — that would incorrectly link the substring concept "mouse".
          if (
            variantHit.toLowerCase() === sourceLower &&
            sourceLower !== baseLower
          ) {
            queue.push(sourceLower);
          }
          // Do NOT enqueue baseLower here — that would wrongly link unrelated chips
          // (e.g. "whole mouse" variant hit would enqueue "mice" base chip).
          // The base term is enqueued via the sourceLower===token path above when needed.
        }
      }
    }
  }

  return visited;
}

function applySynonymLabelsToggle(
  selectedSynonyms,
  termSynonyms,
  labelLowers,
  isNowSelected,
  allowTerm = ALLOW_ALL_TERMS
) {
  const ts =
    termSynonyms && typeof termSynonyms === 'object' ? termSynonyms : {};
  let next = { ...selectedSynonyms };

  for (const [term, availableSynonyms] of Object.entries(ts)) {
    if (!allowTerm(term)) continue;
    const cur = next[term] || [];
    const matching = labelsInListByLower(availableSynonyms, labelLowers);
    if (matching.length === 0) continue;

    next[term] = isNowSelected
      ? uniqStringsCi([...cur, ...matching])
      : cur.filter(s => !labelLowers.has(s.toLowerCase()));
  }

  return next;
}

function applyPluralLabelsToggle(
  selectedPluralVariants,
  termPlurals,
  labelLowers,
  isNowSelected,
  allowTerm = ALLOW_ALL_TERMS
) {
  const tp = termPlurals && typeof termPlurals === 'object' ? termPlurals : {};
  let next = { ...selectedPluralVariants };

  for (const [term, availableVariants] of Object.entries(tp)) {
    if (!allowTerm(term)) continue;
    const cur = next[term] || [];
    const matching = labelsInListByLower(availableVariants, labelLowers);
    if (matching.length === 0) continue;

    next[term] = isNowSelected
      ? uniqStringsCi([...cur, ...matching])
      : cur.filter(v => !labelLowers.has(v.toLowerCase()));
  }

  return next;
}

/**
 * True when plural chip text is the same phrase as the synonym, with a recognised
 * last-token singular/plural pair (e.g. whole mouse ↔ whole mice, mouse ↔ mice).
 */
function phraseSynonymMatchesPluralChip(synPhrase, pluralChip) {
  const s = synPhrase.toLowerCase().trim();
  const pl = pluralChip.toLowerCase().trim();
  if (s === pl) return true;
  const ws = s.split(/\s+/).filter(Boolean);
  const wp = pl.split(/\s+/).filter(Boolean);
  if (ws.length === 0 || wp.length === 0 || ws.length !== wp.length) {
    return false;
  }
  for (let i = 0; i < ws.length - 1; i++) {
    if (ws[i] !== wp[i]) return false;
  }
  const lastS = ws[ws.length - 1];
  const lastP = wp[wp.length - 1];
  if (lastS === lastP) return true;
  const irregular = [
    ['mouse', 'mice'],
    ['cat', 'cats'],
    ['rat', 'rats'],
  ];
  return irregular.some(([a, b]) => lastS === a && lastP === b);
}

/** Plural side-effects when a synonym chip is toggled (per base-term row). */
function applySynonymTogglePluralSideEffects(
  interp,
  term,
  canonicalSyn,
  isNowSelected,
  selectedPluralVariants
) {
  const sources = interp?.termPluralSources?.[term] || {};
  let derivedPlurals = [];
  for (const [sourceKey, variants] of Object.entries(sources)) {
    if (sourceKey.toLowerCase() === canonicalSyn.toLowerCase()) {
      derivedPlurals = uniqStringsCi([
        ...derivedPlurals,
        ...(Array.isArray(variants) ? variants : []),
      ]);
    }
  }

  const termPluralsList = interp?.termPlurals?.[term] || [];

  for (const [, variants] of Object.entries(sources)) {
    const hit = (variants || []).find(
      v => v.toLowerCase() === canonicalSyn.toLowerCase()
    );
    if (!hit) continue;
    for (const p of termPluralsList) {
      if (phraseSynonymMatchesPluralChip(hit, p)) {
        derivedPlurals.push(p);
      }
    }
  }
  derivedPlurals = uniqStringsCi(derivedPlurals);

  const overlapPlurals = termPluralsList.filter(
    p => p.toLowerCase() === canonicalSyn.toLowerCase()
  );
  const toSync = uniqStringsCi([...derivedPlurals, ...overlapPlurals]);

  const next = { ...selectedPluralVariants };
  if (toSync.length > 0) {
    const cur = next[term] || [];
    next[term] = isNowSelected
      ? uniqStringsCi([...cur, ...toSync])
      : cur.filter(v => !toSync.some(t => t.toLowerCase() === v.toLowerCase()));
  }
  return next;
}

export function applySynonymChipToggleSelection(
  interp,
  selectedSynonyms,
  selectedPluralVariants,
  seedLabels,
  isNowSelected,
  allowTerm = ALLOW_ALL_TERMS
) {
  const linkedLabelLowers = resolveLinkedLabelLowersFromPluralSources(
    interp,
    seedLabels,
    allowTerm
  );
  const termSynonyms = interp?.termSynonyms || {};
  const termPlurals = interp?.termPlurals || {};

  let nextSelectedSynonyms = applySynonymLabelsToggle(
    selectedSynonyms,
    termSynonyms,
    linkedLabelLowers,
    isNowSelected,
    allowTerm
  );
  let nextSelectedPluralVariants = applyPluralLabelsToggle(
    selectedPluralVariants,
    termPlurals,
    linkedLabelLowers,
    isNowSelected,
    allowTerm
  );

  const termPluralSourcesMap = interp?.termPluralSources || {};
  const termsForPluralFx = new Set([
    ...Object.keys(termSynonyms),
    ...Object.keys(termPluralSourcesMap),
  ]);
  for (const term of termsForPluralFx) {
    if (!allowTerm(term)) continue;
    const canonicalSyns = labelsAndSourceKeysForPluralSideEffects(
      term,
      termSynonyms,
      termPluralSourcesMap,
      linkedLabelLowers
    );
    for (const canonicalSyn of canonicalSyns) {
      nextSelectedPluralVariants = applySynonymTogglePluralSideEffects(
        interp,
        term,
        canonicalSyn,
        isNowSelected,
        nextSelectedPluralVariants
      );
    }
  }

  return {
    selectedSynonyms: nextSelectedSynonyms,
    selectedPluralVariants: nextSelectedPluralVariants,
  };
}

export function applyPluralChipToggleSelection(
  interp,
  selectedSynonyms,
  selectedPluralVariants,
  seedLabels,
  isNowSelected,
  allowTerm = ALLOW_ALL_TERMS
) {
  const linkedLabelLowers = resolveLinkedLabelLowersFromPluralSources(
    interp,
    seedLabels,
    allowTerm
  );

  const termPlurals = interp?.termPlurals || {};

  const nextSelectedPluralVariants = applyPluralLabelsToggle(
    selectedPluralVariants,
    termPlurals,
    linkedLabelLowers,
    isNowSelected,
    allowTerm
  );

  return {
    selectedSynonyms,
    selectedPluralVariants: nextSelectedPluralVariants,
  };
}
